/*!
 * StudiFi calc engine — ISA / LISA / pension allowance mechanics
 *
 * MIT when lifted. Pure functions.
 *
 * These functions don't decide what a user should do — they compute
 * what they *can* do and what the tax/bonus consequences would be.
 */

/**
 * Check whether the user can open a LISA. Age 18–39 inclusive.
 *
 * @param {number} age
 * @param {object} isaLisaData
 * @returns {boolean}
 */
export function canOpenLISA(age, isaLisaData) {
  return age >= isaLisaData.lisaOpenAgeMin && age <= isaLisaData.lisaOpenAgeMax;
}

/**
 * Compute the LISA outcome for a proposed contribution.
 *
 * @param {number} contribution   Amount user wants to put in this tax year (£)
 * @param {object} isaLisaData
 * @returns {{
 *   contribution: number,
 *   allowedContribution: number,
 *   bonus: number,
 *   totalIntoLISA: number,
 *   returnMultiple: number
 * }}
 */
export function lisaBonusFor(contribution, isaLisaData) {
  const allowed = Math.min(contribution, isaLisaData.lisaAnnualLimit);
  const bonus = allowed * isaLisaData.lisaGovernmentBonusRate;
  return {
    contribution,
    allowedContribution: allowed,
    bonus,
    totalIntoLISA: allowed + bonus,
    returnMultiple: allowed > 0 ? (allowed + bonus) / allowed : 0,
  };
}

/**
 * Compute the net cost of an early LISA withdrawal (not for a first
 * home, not age 60+, not terminal illness). Government reclaims a 25%
 * penalty which — because it's applied to the withdrawal amount not
 * the bonus — leaves the user slightly worse off than they started.
 *
 * @param {number} withdrawalAmount    Amount to withdraw (£)
 * @param {object} isaLisaData
 * @returns {{
 *   grossWithdrawal: number,
 *   penalty: number,
 *   netReceived: number,
 *   effectiveLossVsContribution: number    // negative = loss
 * }}
 */
export function lisaEarlyWithdrawal(withdrawalAmount, isaLisaData) {
  const penalty = withdrawalAmount * isaLisaData.lisaEarlyWithdrawalPenaltyRate;
  const netReceived = withdrawalAmount - penalty;
  // If the user contributed £X → got £1.25X in the LISA → withdraw £1.25X
  //   → penalty 25% × £1.25X = £0.3125X → netReceived = £0.9375X
  //   → loss vs original contribution = -6.25% of original £X
  const originalContribution = withdrawalAmount / (1 + isaLisaData.lisaGovernmentBonusRate);
  return {
    grossWithdrawal: withdrawalAmount,
    penalty,
    netReceived,
    effectiveLossVsContribution: netReceived - originalContribution,
  };
}

/**
 * Total ISA allowance remaining after LISA contributions.
 * LISA counts INSIDE the £20k ISA allowance.
 *
 * @param {number} lisaContribution   £/tax year already going to LISA
 * @param {object} isaLisaData
 * @returns {number}
 */
export function remainingISAAllowance(lisaContribution, isaLisaData) {
  const used = Math.min(lisaContribution, isaLisaData.lisaAnnualLimit);
  return Math.max(0, isaLisaData.isaAnnualLimit - used);
}

/**
 * Pension annual allowance after high-earner taper.
 *
 * @param {number} adjustedIncome    Broadly: total income + all pension inputs
 * @param {object} pensionData
 * @returns {number}                 £/yr
 */
export function taperedAnnualAllowance(adjustedIncome, pensionData) {
  const {
    annualAllowance,
    annualAllowanceTaperStart,
    annualAllowanceTaperFloor,
  } = pensionData;

  if (adjustedIncome <= annualAllowanceTaperStart) return annualAllowance;
  const over = adjustedIncome - annualAllowanceTaperStart;
  const reduction = over / 2;
  return Math.max(annualAllowanceTaperFloor, annualAllowance - reduction);
}
